import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Radio } from "@/components/Radio/Radio";
import { ROUTE_CONFIG } from "@/routes/config";
import { classNames } from "@/utils/classNames";

import {
	emailSeatUsage,
	SendingSeatSlotData,
	sendingSeatAvailability,
} from "./sendingSeatAvailability";

interface InviteSendingUserFieldProps {
	/** Whether the invitee is being invited as a sending user. */
	value: boolean;
	onChange: (value: boolean) => void;
	slotData?: SendingSeatSlotData | null;
	/** Email addresses only exist as a concept when the sequencer is on. */
	isSequencerActive?: boolean;
}

interface SendingOptionProps {
	label: string;
	description: string;
	isChecked: boolean;
	isDisabled?: boolean;
	testId: string;
	onSelect: () => void;
}

const SendingOption: FunctionComponent<SendingOptionProps> = ({
	label,
	description,
	isChecked,
	isDisabled,
	testId,
	onSelect,
}) => (
	<div
		className={classNames(
			"rounded-xl border p-3 duration-200",
			isChecked ? "border-brand-light bg-blue-action-200/30" : "border-gray-200",
			isDisabled ? "" : "hover:border-brand-light"
		)}
	>
		<Radio isChecked={isChecked} isDisabled={isDisabled} testId={testId} onChange={onSelect}>
			<div className="flex flex-col">
				<span className="text-sm font-medium text-text">{label}</span>
				<span className="text-xs text-text-meta">{description}</span>
			</div>
		</Radio>
	</div>
);

/**
 * The sending-user choice on the invite modal (PREC-3787).
 *
 * Before this, every invited user landed as non-sending and an admin had to promote them from the
 * Users table afterwards — and the invitee meanwhile hit the onboarding tour's "you are not a
 * sending user" Connect step. Making the choice here also means the seat has to be *visible* here,
 * which is the other half of the ticket.
 */
export const InviteSendingUserField: FunctionComponent<InviteSendingUserFieldProps> = ({
	value,
	onChange,
	slotData,
	isSequencerActive,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`inviteUser.modal.${key}`);

	const seats = sendingSeatAvailability(slotData);
	const emails = emailSeatUsage(slotData);

	return (
		<div className="flex flex-col gap-2">
			<span className="text-sm font-medium text-gray-900">{ts("sendingUserLabel")}</span>

			<SendingOption
				description={t("basics.sendingLabelDesc")}
				isChecked={value}
				isDisabled={!seats.hasFreeSeat}
				label={t("basics.sendingLabel")}
				testId="invite-sending-user"
				onSelect={() => onChange(true)}
			/>

			<SendingOption
				description={t("basics.nonSendingLabelDesc")}
				isChecked={!value}
				label={t("basics.nonSendingLabel")}
				testId="invite-non-sending-user"
				onSelect={() => onChange(false)}
			/>

			<p className="text-xs text-text-meta">
				{t("inviteUser.modal.seatsUsed", { used: seats.used, total: seats.capacity })}
			</p>

			{isSequencerActive && (
				<p className="text-xs text-text-meta">
					{t("inviteUser.modal.includesEmail", { used: emails.used, total: emails.capacity })}
				</p>
			)}

			{!seats.hasFreeSeat && (
				<div className="rounded-xl bg-background-warning p-3">
					<p className="text-xs text-text-warning">{ts("seatsFull")}</p>
					<Link
						className="text-xs font-semibold text-brand-light hover:underline"
						to={ROUTE_CONFIG.SELECT_SUBSCRIPTION}
					>
						{ts("seatsFullCta")}
					</Link>
				</div>
			)}
		</div>
	);
};
